import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import "./BlogCard.css";

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return fallback;
};

const normalizeTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        asText(item?.title ?? item?.name ?? item?.label ?? item),
      )
      .filter(Boolean)
      .slice(0, 3);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 3);
  }

  return [];
};

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

const BlogCard = ({ Blog = {}, sequence = 1, isActive = false }) => {
  const title = asText(Blog.title ?? Blog.name, "Axiomos insight");
  const description = asText(
    Blog.descriptionsmall ??
      Blog.shortDescription ??
      Blog.summary ??
      Blog.description,
    "A practical perspective on building better digital products and technology-led businesses.",
  );
  const category = asText(
    Blog.industry ?? Blog.category ?? Blog.topic,
    "Technology insight",
  );
  const image = asText(
    Blog.photo ?? Blog.image ?? Blog.coverImage ?? Blog.thumbnail,
  );
  const externalLink = asText(Blog.link ?? Blog.sourceUrl);
  const identifier = Blog.slug ?? Blog.id;
  const articlePath =
    identifier !== undefined && identifier !== null
      ? `/blog/${identifier}`
      : "/blog";
  const publishedDate = asText(
    Blog.publishedDate ?? Blog.publishedAt ?? Blog.date,
  );
  const readTime = asText(Blog.readTime ?? Blog.readingTime);
  const author = asText(Blog.author ?? Blog.authorName);
  const tags = normalizeTags(Blog.tags ?? Blog.keywords);
  const fallback =
    title.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "AX";
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [image]);

  return (
    <article className="blog-card">
      <div className="blog-card__media">
        <ScrollLink
          to={articlePath}
          className="blog-card__media-link"
          aria-label={`Read ${title}`}
        >
          {image && !imageFailed ? (
            <img
              src={image}
              alt={`${title} article cover`}
              className="blog-card__image"
              loading={isActive ? "eager" : "lazy"}
              decoding="async"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span className="blog-card__image-fallback" aria-hidden="true">
              {fallback}
            </span>
          )}
        </ScrollLink>

        <div className="blog-card__media-overlay" aria-hidden="true" />

        <div className="blog-card__media-top">
          <span className="blog-card__sequence" aria-hidden="true">
            Insight / {String(sequence).padStart(2, "0")}
          </span>

          {externalLink && (
            <a
              href={externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="blog-card__external-link"
              aria-label={`Open the external source for ${title}`}
            >
              Source
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                aria-hidden="true"
              />
            </a>
          )}
        </div>
      </div>

      <div className="blog-card__content">
        <div className="blog-card__meta">
          <span>{category}</span>
          {publishedDate && <time>{publishedDate}</time>}
          {readTime && <small>{readTime}</small>}
        </div>

        <h3 className="blog-card__title">
          <ScrollLink to={articlePath}>{title}</ScrollLink>
        </h3>

        <p className="blog-card__description">{description}</p>

        {tags.length > 0 && (
          <ul className="blog-card__tags" aria-label="Article topics">
            {tags.map((tag, index) => (
              <li key={`${tag}-${index}`}>{tag}</li>
            ))}
          </ul>
        )}

        <div className="blog-card__footer">
          {author && (
            <p className="blog-card__author">
              <span aria-hidden="true">
                {author.slice(0, 1).toUpperCase()}
              </span>
              <small>
                Written by
                <strong>{author}</strong>
              </small>
            </p>
          )}

          <ScrollLink to={articlePath} className="blog-card__cta">
            Read insight
            <span aria-hidden="true">
              <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </ScrollLink>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
